<script setup lang="ts">
import { computed, ref } from 'vue'
import { useVModel } from '@vueuse/core'
import { formatPhoneNumber } from '~/utils/parsePhoneNumber'

const props = defineProps<{
    defaultValue?: string | number
    modelValue?: string | number
    placeholder?: string
    id: string
    validation?: boolean | null
    label?: string
    phoneNumber?: boolean
    type: 'text' | 'number' | 'password' | 'email' | 'tel'
    errorMessage?: string
    max?: string
    disabled?: boolean
}>()

const emits = defineEmits<{
    (e: 'update:modelValue', payload: string | number): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: props.defaultValue,
})

const isFocused = ref(false)
const isClicked = ref(false)
const isDisabled = computed(() => Boolean(props.disabled))
const isTyping = ref(false)
let typingTimeout: ReturnType<typeof setTimeout> | null = null

const isFilled = computed(() => Boolean(modelValue.value))

const wrapperClass = computed(() => {
    if (isDisabled.value) {
        return 'border-gray-300 bg-gray-100'
    }
    if (props.validation) {
        return 'border-plus-lp-danger'
    }
    if (isTyping.value) {
        return 'border-[#183A4C]'
    }
    if (isFocused.value) {
        return 'border-[#00283D] ring-offset-2 ring-2 ring-[#0067DF]'
    }
    if (isFilled.value) {
        return 'border-[#D3D7DF]'
    }
    if (isClicked.value) {
        return 'border-[#00283D] ring-offset-0 ring-0 ring-white'
    }
    return 'border-[#C0C5D0] hover:border-[#899EA9]'
})

function handleInput(event: Event) {
    if (props.phoneNumber) {
        formatPhoneNumber(event.target)
    }
    const target = event.target as HTMLInputElement
    modelValue.value = target.value
    isTyping.value = true

    if (typingTimeout) {
        clearTimeout(typingTimeout)
        typingTimeout = setTimeout(() => {
            isTyping.value = false
        }, 500)
    }
}

function handleFocus() {
    isFocused.value = true
    isClicked.value = false
}

function handleBlur() {
    isFocused.value = false
}

function handleClick() {
    isClicked.value = true
    isFocused.value = false
}

onBeforeUnmount(() => {
    if (typingTimeout) {
        clearTimeout(typingTimeout)
    }
})
</script>

<template>
    <div>
        <label
            :for="id"
            class="label-sm mb-2 font-figtree text-plus-lp-dark-blue"
        >
            {{ label }}
        </label>
        <div
            :class="wrapperClass"
            class="linear lp-paragraph-md flex h-12 w-full rounded-md border px-3 py-2 font-medium text-plus-lp-dark-blue-500 transition-colors delay-0 duration-200"
        >
            <div
                v-if="phoneNumber"
                class="flex items-center pr-2"
            >
                <span class="lp-paragraph-md">+48</span>
            </div>
            <input
                :id="id"
                v-model="modelValue"
                :placeholder="placeholder"
                :type="type"
                :max="max"
                :disabled="isDisabled"
                class="w-full bg-transparent text-plus-lp-dark-blue focus:outline-none"
                @input="handleInput"
                @focus="handleFocus"
                @blur="handleBlur"
                @click="handleClick"
            />
        </div>
        <span
            v-if="validation"
            class="lp-paragraph-sm mt-1 text-plus-lp-danger"
        >
            {{ errorMessage }}
        </span>
    </div>
</template>

<style scoped>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
</style>
